import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Toggle from "./Toggle";
import TranslatableField from "./TranslatableField";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { createServiceCategory, updateServiceCategory } from "../services/serviceService";

const EMPTY = {
  name: { en: "", ar: "" },
  description: { en: "", ar: "" },
  parentId: "",
  isVisibleOnline: true,
  isActive: true,
  sortOrder: "0",
};

const toForm = (category) =>
  category
    ? {
        name: category.translations?.name ?? { en: "", ar: "" },
        description: category.translations?.description ?? { en: "", ar: "" },
        parentId: category.parent_id ?? "",
        isVisibleOnline: Boolean(category.is_visible_online),
        isActive: Boolean(category.is_active),
        sortOrder: String(category.sort_order ?? "0"),
      }
    : EMPTY;

/**
 * Create or edit one service group. `parents` are the top-level groups: nesting
 * goes one level, so a subgroup cannot itself be a parent.
 */
export default function ServiceCategoryModal({ category, parents = [], open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(category);

  useEffect(() => {
    if (!open) return;

    setValues(toForm(category));
    setErrors({});
    setBanner("");
  }, [open, category]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    setBanner("");

    try {
      const saved = editing
        ? await updateServiceCategory(category.id, values)
        : await createServiceCategory(values);

      onSaved(saved, editing);
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("serviceCategories.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // A group cannot sit under itself, and a subgroup cannot become a parent.
  const parentOptions = parents.filter((parent) => parent.id !== category?.id);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-lg rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h2 className="text-lg font-semibold">
              {editing ? t("serviceCategories.form.editTitle") : t("serviceCategories.form.addTitle")}
            </h2>
            <button type="button" onClick={onClose} className="text-sm text-ink-2 hover:text-ink">
              {t("common.close")}
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            {banner && (
              <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            <TranslatableField
              name="name"
              label={t("serviceCategories.form.name")}
              value={values.name}
              onChange={(name) => setValues({ ...values, name })}
              errors={errors}
              required
            />

            <TranslatableField
              name="description"
              label={t("serviceCategories.form.description")}
              value={values.description}
              onChange={(description) => setValues({ ...values, description })}
              errors={errors}
              textarea
              rows={2}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="field-label" htmlFor="category-parent">
                  {t("serviceCategories.form.parent")}
                </label>
                <select
                  id="category-parent"
                  className="field-input"
                  value={values.parentId}
                  onChange={(e) => setValues({ ...values, parentId: e.target.value })}
                >
                  <option value="">{t("serviceCategories.form.topLevel")}</option>
                  {parentOptions.map((parent) => (
                    <option key={parent.id} value={parent.id}>
                      {parent.name}
                    </option>
                  ))}
                </select>
                {errors.parent_id?.[0] && <p className="field-error">{errors.parent_id[0]}</p>}
              </div>

              <div>
                <label className="field-label" htmlFor="category-order">
                  {t("serviceCategories.form.sortOrder")}
                </label>
                <input
                  id="category-order"
                  type="number"
                  min="0"
                  dir="ltr"
                  className="field-input"
                  value={values.sortOrder}
                  onChange={(e) => setValues({ ...values, sortOrder: e.target.value })}
                />
              </div>
            </div>

            <Toggle
              label={t("serviceCategories.form.visibleOnline")}
              hint={t("serviceCategories.form.visibleOnlineHint")}
              checked={values.isVisibleOnline}
              onChange={(checked) => setValues({ ...values, isVisibleOnline: checked })}
            />

            <Toggle
              label={t("serviceCategories.form.active")}
              checked={values.isActive}
              onChange={(checked) => setValues({ ...values, isActive: checked })}
            />
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
